import React from 'react';

import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Heading } from '@/components/ui/catalyst/heading';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { TreatmentRecordRequestChannelType } from '@/services/api/generated/treatmentRecordRequestsService';

import { VoiceAICallFormValues } from './hooks/useVoiceAICall';

const REQUEST_METHOD_OPTIONS: TreatmentRecordRequestChannelType[] = [
  'portal',
  'human',
  'email',
  'fax',
  'mail',
];

const getTargetLabel = (method: TreatmentRecordRequestChannelType): string => {
  if (method === 'fax') return 'Fax number';
  if (method === 'email') return 'Email address';
  if (method === 'mail') return 'Mailing address';
  if (method === 'portal') return 'Portal Information';
  if (method === 'human') return 'Target (optional)';
  return 'Target (optional)';
};

interface VoiceAICallFormProps {
  values: VoiceAICallFormValues;
  onChange: (
    section: keyof VoiceAICallFormValues,
    field: string,
    value: string,
  ) => void;
}

export const VoiceAICallForm: React.FC<VoiceAICallFormProps> = ({
  values,
  onChange,
}) => {
  return (
    <div className="space-y-6">
      {/* Patient Information */}
      <div className="rounded-lg border border-zinc-200 bg-white p-6">
        <Heading level={3} className="mb-4">
          Patient Information
        </Heading>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label htmlFor="client_name">Client Name</Label>
            <Input
              id="client_name"
              aria-label="Client Name"
              value={values.patient_information.client_name}
              onChange={(e) =>
                onChange('patient_information', 'client_name', e.target.value)
              }
              placeholder="Full name"
            />
          </Field>
          <Field>
            <Label htmlFor="client_dob">Date of Birth</Label>
            <Input
              id="client_dob"
              aria-label="Client Date of Birth"
              type="date"
              value={values.patient_information.client_date_of_birth}
              onChange={(e) =>
                onChange(
                  'patient_information',
                  'client_date_of_birth',
                  e.target.value,
                )
              }
            />
          </Field>
        </div>
      </div>

      {/* Request Details */}
      <div className="rounded-lg border border-zinc-200 bg-white p-6">
        <Heading level={3} className="mb-4">
          Request Details
        </Heading>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label htmlFor="request_method">Request Method</Label>
            <Select
              id="request_method"
              aria-label="Request Method"
              value={values.request_details.request_method}
              onChange={(e) =>
                onChange(
                  'request_details',
                  'request_method',
                  e.target.value as TreatmentRecordRequestChannelType,
                )
              }
            >
              {REQUEST_METHOD_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label htmlFor="request_sent_at">Request Sent Date</Label>
            <Input
              id="request_sent_at"
              aria-label="Request Sent Date"
              type="datetime-local"
              value={new Date(values.request_details.request_sent_at)
                .toISOString()
                .slice(0, 16)}
              onChange={(e) =>
                onChange(
                  'request_details',
                  'request_sent_at',
                  new Date(e.target.value).toISOString(),
                )
              }
            />
          </Field>
          <Field>
            <Label htmlFor="request_method_target">
              {getTargetLabel(values.request_details.request_method)}
            </Label>
            <Input
              id="request_method_target"
              aria-label="Request Method Target"
              value={values.request_details.request_method_target}
              onChange={(e) =>
                onChange(
                  'request_details',
                  'request_method_target',
                  e.target.value,
                )
              }
              placeholder={getTargetLabel(
                values.request_details.request_method,
              )}
            />
          </Field>
          <Field>
            <Label htmlFor="contact_number">Contact Number</Label>
            <Input
              id="contact_number"
              aria-label="Contact Number"
              type="tel"
              value={values.request_details.contact_number}
              onChange={(e) =>
                onChange('request_details', 'contact_number', e.target.value)
              }
              placeholder="(555) 555-5555"
            />
          </Field>
        </div>
      </div>
    </div>
  );
};
